import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/libs/utils'

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        success: 'border-transparent bg-success text-success-foreground hover:bg-success/80',
        warning: 'border-warning/50 bg-warning/10 text-warning-foreground',
        outline: 'text-foreground',
        // Soft-tint variants: light color-tinted background + colored text/border,
        // following the same visual pattern as `warning` above but keyed to each
        // color's own token (colored text rather than a `-foreground` token).
        successSoft: 'border-success/20 bg-success/10 text-success',
        destructiveSoft: 'border-destructive/20 bg-destructive/10 text-destructive',
        primarySoft: 'border-primary/20 bg-primary/10 text-primary',
        accentSoft: 'border-accent/20 bg-accent/10 text-accent',
        warningSoft: 'border-warning/20 bg-warning/10 text-warning',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge, badgeVariants }
